import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 10;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [phase, setPhase] = useState('checking'); // checking | success | timeout | error
  const [orderId, setOrderId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setPhase('error');
      setErrorMsg('Missing session reference. If you completed payment, check your orders page.');
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      // No session — bounce to login while preserving deep link intent
      navigate('/login');
      return;
    }

    let cancelled = false;

    const poll = async () => {
      attemptRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/stripe/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const ps = (res.data?.payment_status || '').toLowerCase();
        const orderStatus = (res.data?.status || '').toLowerCase();
        const oid = res.data?.order_id || null;
        if (oid) setOrderId(oid);

        // Stripe lib returns payment_status = "paid" once paid, status = "complete" / "open"
        // The /status endpoint also auto-flips the order to documents_pending — so we treat
        // "paid" as authoritative success.
        if (ps === 'paid' || orderStatus === 'complete' || orderStatus === 'completed') {
          setPhase('success');
          return;
        }

        if (attemptRef.current >= MAX_ATTEMPTS) {
          setPhase('timeout');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (attemptRef.current >= MAX_ATTEMPTS) {
          setPhase('timeout');
          return;
        }
        // Transient errors — keep polling
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, navigate]);

  const goToOrders = () => {
    if (orderId) {
      navigate(`/dashboard/orders/${orderId}`);
    } else {
      navigate('/dashboard/orders');
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0b1310] px-4"
      data-testid="payment-success-page"
    >
      <div className="max-w-md w-full bg-[#111827] border border-gray-800 rounded-xl p-8 text-center">
        {phase === 'checking' && (
          <>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#059669]/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#059669] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-checking-title">
              Confirming your payment
            </h1>
            <p className="text-gray-400 text-sm">
              This usually takes a few seconds. Please don&apos;t close this window.
            </p>
            <p
              className="text-gray-600 text-xs mt-4"
              data-testid="payment-poll-attempt"
            >
              Attempt {attemptRef.current} of {MAX_ATTEMPTS}
            </p>
          </>
        )}

        {phase === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#059669]/15 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-[#059669]" />
            </div>
            <h1
              className="text-2xl font-bold text-white mb-2"
              data-testid="payment-success-title"
            >
              Payment confirmed
            </h1>
            <p className="text-gray-400 text-sm mb-6">
              Thanks — we&apos;ve received your payment and your order is now on the queue.
            </p>
            <button
              onClick={goToOrders}
              className="w-full px-4 py-3 bg-[#059669] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity"
              data-testid="go-to-orders-btn"
            >
              Go to Orders
            </button>
          </>
        )}

        {phase === 'timeout' && (
          <>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-500/15 flex items-center justify-center">
              <Clock className="w-9 h-9 text-amber-400" />
            </div>
            <h1
              className="text-2xl font-bold text-white mb-2"
              data-testid="payment-timeout-title"
            >
              Almost there
            </h1>
            <p className="text-gray-400 text-sm mb-6">
              Your payment was received. It may take a few minutes to reflect. Check your
              orders page.
            </p>
            <button
              onClick={goToOrders}
              className="w-full px-4 py-3 bg-[#059669] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity"
              data-testid="go-to-orders-btn"
            >
              Go to Orders
            </button>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-500/15 flex items-center justify-center">
              <AlertCircle className="w-9 h-9 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-error-title">
              We couldn&apos;t verify the payment
            </h1>
            <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="w-full px-4 py-3 bg-[#059669] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity"
              data-testid="go-to-orders-btn"
            >
              Go to Orders
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
